import React from 'react'
import { Button, Card } from 'react-bootstrap'
import { observer } from 'mobx-react'
import { observable, action } from 'mobx'
import { APPConfig, convertToBoolean } from 'config/appConfig'
import {
  i18nTranslate,
  isExpressCheckout,
  trackCheckoutCouponCodeEvents,
  getLocalStorage,
  setLocalStorage,
  deleteFromLocalStorage,
} from 'src/utils'
import { publish, PUBSUB } from 'src/pubsub'
import { checkoutContainer, cartContainer } from 'src/models'
import { toastState } from 'src/views/components'
import { dateFormatDeps, isFetchAllPromoCodeEnabled } from 'src/deps'
import {
  PromoStackingService,
  getScopedAppliedPromoKey,
  migrateOldPromoStorageKey,
} from 'src/utils/promoStackingService'
import {
  IoIosClose as CloseIcon,
  IoIosArrowDown as DownwardArrowIcon,
  IoIosArrowUp as UpwardArrowIcon,
  IoMdSettings as SettingIcon,
} from 'react-icons/io'
import './styles.scss'

@observer
class MyaccountWallet extends React.Component {
  @observable showMore = true
  @observable showMoreAnnouncement = ''
  showMoreDefaultLength = 2
  cartResponseLoadPromise = null
  @observable promoList = []
  @observable isCallInProgress = false
  @observable isToResetForm = false
  @observable disablePromoStacking =
    convertToBoolean(APPConfig?.getAppConfig()?.disablePromoStacking) || false
  @observable promoCodeRegexToAllowStacking =
    APPConfig?.getAppConfig()?.promoCodeRegexToAllowStacking || ''
  @observable isReplacingPromo = false
  @observable toShowAlertMsg = false
  @observable previousAppliedPromo = ''
  @observable currentAppliedPromo = ''
  @observable showAppliedPromoList = false
  @observable hasRunPromoCleanup = false

  // Initialize service once to avoid creating new instances on every access
  promoStackingService = new PromoStackingService({
    disablePromoStacking: this.disablePromoStacking,
    promoCodeRegexToAllowStacking: this.promoCodeRegexToAllowStacking,
  })

  loadPromoCodeList = async () => {
    if (isFetchAllPromoCodeEnabled()) {
      return await cartContainer.promoCodeList({ fetchAllPromoCodes: true })
    }
    return await cartContainer.promoCodeList()
  }

  setAppliedPromoCode = promoCode => {
    if (!this.disablePromoStacking || !promoCode) return
    const setPromoStorageKey = getScopedAppliedPromoKey(
      this.disablePromoStacking
    )
    if (setPromoStorageKey) {
      setLocalStorage(setPromoStorageKey, promoCode)
    } else {
      setLocalStorage('appliedPromoCode', promoCode)
    }
  }

  deleteAppliedPromoCode = (isFrom = '') => {
    const deletePromoStorageKey = getScopedAppliedPromoKey(
      this.disablePromoStacking
    )
    if (deletePromoStorageKey) {
      deleteFromLocalStorage(`${deletePromoStorageKey}_previous`)
      deleteFromLocalStorage(`${deletePromoStorageKey}_current`)
      if (isFrom === 'delete') {
        deleteFromLocalStorage(deletePromoStorageKey)
      }
    } else {
      deleteFromLocalStorage('appliedPromoPrevious')
      deleteFromLocalStorage('appliedPromoCurrent')
      if (isFrom === 'delete') {
        deleteFromLocalStorage('appliedPromoCode')
      }
    }
  }

  getStoredPromoData = promoStorageKey => {
    return {
      storedPromo: promoStorageKey
        ? getLocalStorage(promoStorageKey)
        : getLocalStorage('appliedPromoCode'),
      storedPrev: promoStorageKey
        ? getLocalStorage(`${promoStorageKey}_previous`)
        : getLocalStorage('appliedPromoPrevious'),
      storedCurrent: promoStorageKey
        ? getLocalStorage(`${promoStorageKey}_current`)
        : getLocalStorage('appliedPromoCurrent'),
    }
  }

  restorePromoMessageState = (storedPrev, storedCurrent) => {
    if (!storedPrev && !storedCurrent) {
      return
    }

    this.previousAppliedPromo = storedPrev || ''
    this.currentAppliedPromo = storedCurrent || ''

    if (this.previousAppliedPromo || this.currentAppliedPromo) {
      this.toShowAlertMsg = true
    }

    if (this.previousAppliedPromo === this.currentAppliedPromo) {
      this.deleteAppliedPromoCode()
    }
  }

  syncExistingAppliedPromo = () => {
    const appliedCodes =
      cartContainer?.cartResponse?.promotionDetails?.codes || []
    const existingCode = Array.isArray(appliedCodes)
      ? appliedCodes?.[0]?.code
      : ''

    if (existingCode) {
      this.setAppliedPromoCode(existingCode)
      return true
    }

    return false
  }

  shouldCleanupMultiplePromos = cartResponse => {
    const existingAppliedCodes = cartResponse?.promotionDetails?.codes || []
    return this.disablePromoStacking && existingAppliedCodes.length > 1
  }

  cleanupMultipleAppliedPromos = async () => {
    if (this.hasRunPromoCleanup) {
      return
    }

    this.hasRunPromoCleanup = true
    const deleteResponse = await checkoutContainer?.deletePromo?.()
    if (checkoutContainer.isSuccessResponse(deleteResponse)) {
      this.deleteAppliedPromoCode('delete')
      toastState.setToastMessage(
        i18nTranslate(
          'response.deletePromocode',
          'Promo code removed successfully'
        ),
        true
      )
    }

    await this.loadPromoCodeList()
  }

  restoreOrApplySinglePromoState = async () => {
    const promoStorageKey = getScopedAppliedPromoKey(this.disablePromoStacking)
    migrateOldPromoStorageKey(promoStorageKey)
    const { storedPromo, storedPrev, storedCurrent } =
      this.getStoredPromoData(promoStorageKey)
    this.restorePromoMessageState(storedPrev, storedCurrent)

    const hasExistingAppliedPromo = this.syncExistingAppliedPromo()
    if (!hasExistingAppliedPromo && storedPromo) {
      await this.handleApplyClick(storedPromo)
    }
  }

  initializeDisableStackingPromoState = async cartResponse => {
    try {
      if (this.shouldCleanupMultiplePromos(cartResponse)) {
        await this.cleanupMultipleAppliedPromos()
      } else {
        await this.restoreOrApplySinglePromoState()
      }
    } catch (e) {
      console.error('initializeDisableStackingPromoState failed', e)
    }

    this.showAppliedPromoList = true
  }

  ensureCartResponseReady = async () => {
    if (!this.cartResponseLoadPromise) {
      this.cartResponseLoadPromise = cartContainer.viewCart().finally(() => {
        this.cartResponseLoadPromise = null
      })
    }
    return this.cartResponseLoadPromise
  }

  componentDidMount() {
    this._isMounted = true
    this.initializeOnMount()
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  initializeOnMount = async () => {
    await this.loadPromoCodeList()
    checkoutContainer.isToDisableSubmitOrderButton = true
    if (this.disablePromoStacking) {
      const cartResponse = await this.ensureCartResponseReady()
      if (!this._isMounted) return
      if (!checkoutContainer.isSuccessResponse(cartResponse)) {
        this.showAppliedPromoList = true
        return
      }
      await this.initializeDisableStackingPromoState(cartResponse)
    }
    await this.handlePaymentOnPromoAddOrRemove({ action: 'init' })
  }
  handleApplyCouponCode = async formData => {
    this.isCallInProgress = true
    let promoCode = formData?.promoCode?.trim?.() || ''
    const response = await checkoutContainer.addPromo(promoCode)
    trackCheckoutCouponCodeEvents({
      coupon: promoCode,
    })
    if (checkoutContainer.isSuccessResponse(response)) {
      formData.promoCode = ''
      toastState.setToastMessage(
        response?.message ||
          i18nTranslate(
            'response.SuccessPromocode',
            'Promo code applied successfully'
          ),
        true
      )
      this.currentAppliedPromo = promoCode
      this.setAppliedPromoCode(promoCode)
      if (this.previousAppliedPromo) this.toShowAlertMsg = true

      publish(PUBSUB.PROMO_CODE_APPLY_REMOVE_EVENT, { promoCode, response })
      await this.handlePaymentOnPromoAddOrRemove({ action: 'add' })
      // TODO EX: need to check if this is required
      if (isExpressCheckout()) {
        await cartContainer.viewCart()
      }
      this.isToResetForm = true
      this.isCallInProgress = false
    } else if (
      response.code === 'EOCAPRC001' ||
      response.status === 'failure'
    ) {
      toastState.setToastMessage(
        i18nTranslate(
          'response.invalidPromocode',
          'The promotion code entered is invalid.'
        ),
        false
      )
      this.isCallInProgress = false
    } else {
      toastState.setToastMessage(
        response?.message ||
          i18nTranslate(
            'response.failurePromocode',
            'unable to add the promo code'
          ),
        false
      )
      this.isCallInProgress = false
    }
    await this.loadPromoCodeList()
    return response
  }

  @action
  handleApplyClick = async promoCode => {
    if (this.isReplacingPromo || this.isCallInProgress) return

    const appliedPromoCodeList =
      cartContainer?.cartResponse?.promotionDetails?.codes || []

    // Use service to determine strategy
    const strategy = this.promoStackingService.determineReplacementStrategy(
      promoCode,
      appliedPromoCodeList
    )

    if (strategy.alreadyApplied) {
      this.handleAlreadyAppliedPromo(promoCode)
      return
    }

    if (strategy.canApplyDirectly || !strategy.shouldReplace) {
      await this.applyPromoWithoutReplacement(promoCode)
      return
    }

    if (strategy.promoToReplace) {
      await this.handlePromoReplacement(strategy, promoCode)
    }
  }

  handleAlreadyAppliedPromo = promoCode => {
    if (this.disablePromoStacking) {
      this.previousAppliedPromo = promoCode
      this.currentAppliedPromo = promoCode
      this.toShowAlertMsg = true
      this.deleteAppliedPromoCode()
      return
    }

    toastState.setToastMessage(
      i18nTranslate(
        'response.alreadyAppliedPromo',
        'This promo code is already applied'
      ),
      false
    )
  }

  applyPromoWithoutReplacement = async promoCode => {
    await this.handleApplyCouponCode({ promoCode })
    this.toShowAlertMsg = false
  }

  storeReplacementState = (promoStorageKey, previousPromo, currentPromo) => {
    if (promoStorageKey) {
      setLocalStorage(`${promoStorageKey}_previous`, previousPromo)
      setLocalStorage(`${promoStorageKey}_current`, currentPromo)
      return
    }

    setLocalStorage('appliedPromoPrevious', previousPromo)
    setLocalStorage('appliedPromoCurrent', currentPromo)
  }

  resetReplacementMessageState = () => {
    this.previousAppliedPromo = ''
    this.currentAppliedPromo = ''
    this.toShowAlertMsg = false
    // Pass 'delete' arg to clear ALL promo storage keys (main + _previous + _current)
    // Critical: prevents localStorage from claiming old promo is still applied after recovery fails
    this.deleteAppliedPromoCode('delete')
  }

  handlePromoReplacement = async (strategy, promoCode) => {
    try {
      this.isReplacingPromo = true
      const deleteResponse = await checkoutContainer.deletePromo(
        strategy.promoToReplace
      )

      if (!checkoutContainer.isSuccessResponse(deleteResponse)) {
        toastState.setToastMessage(
          deleteResponse?.message ||
            i18nTranslate(
              'response.failureDeletePromo',
              'Unable to remove the existing promo code'
            ),
          false
        )
        return
      }

      this.previousAppliedPromo = strategy.promoToReplace
      const addResponse = await this.handleApplyCouponCode({ promoCode })
      if (!checkoutContainer.isSuccessResponse(addResponse)) {
        // Add failed - restore old promo directly (not via wrapper) to avoid misleading toast
        const restoreResponse = await checkoutContainer.addPromo(
          strategy.promoToReplace.trim()
        )
        if (!checkoutContainer.isSuccessResponse(restoreResponse)) {
          // Both add and restore failed - old promo lost, silent recovery
          this.resetReplacementMessageState()
        } else {
          // Restore succeeded - old promo is back in cart, no toast needed
          this.currentAppliedPromo = strategy.promoToReplace
          this.previousAppliedPromo = ''
          this.toShowAlertMsg = false
        }
        return
      }

      if (this.disablePromoStacking) {
        const promoStorageKey = getScopedAppliedPromoKey(
          this.disablePromoStacking
        )
        this.storeReplacementState(
          promoStorageKey,
          strategy.promoToReplace,
          promoCode
        )
      }
    } catch (e) {
      console.error('Error replacing promo:', e)
      this.resetReplacementMessageState()
    } finally {
      this.isReplacingPromo = false
    }
  }

  renderSinglePromoMessage = () => {
    if (!this.toShowAlertMsg) {
      return (
        <span>
          {i18nTranslate(
            'promocode.onlyOne',
            'Only one promo code can be applied at a time. Please remove the existing promo code to add a new one.'
          )}
        </span>
      )
    }

    if (this.previousAppliedPromo === this.currentAppliedPromo) {
      return (
        <span>
          {i18nTranslate(
            'promocode.onlyOne',
            'Only one promo code can be applied at a time.'
          )}
        </span>
      )
    }

    return (
      <>
        {i18nTranslate(
          'promocode.replacedPrefix',
          'Only one promo code can be applied at a time. Removed'
        )}{' '}
        <span className="promo-code-inline">{this.previousAppliedPromo}</span>{' '}
        {i18nTranslate('promocode.replacedInfix', 'and applied')}{' '}
        <span className="promo-code-inline">{this.currentAppliedPromo}</span>
      </>
    )
  }

  handleDeleteCouponCode = async promoCode => {
    const response = await checkoutContainer.deletePromo(promoCode)
    if (checkoutContainer.isSuccessResponse(response)) {
      toastState.setToastMessage(
        response?.message ||
          i18nTranslate(
            'response.deletePromocode',
            'Promo code removed successfully'
          ),
        true
      )
      this.previousAppliedPromo = ''
      this.currentAppliedPromo = ''
      this.toShowAlertMsg = false
      if (this.disablePromoStacking) {
        this.deleteAppliedPromoCode('delete')
      }

      publish(PUBSUB.PROMO_CODE_APPLY_REMOVE_EVENT, { promoCode, response })
      await this.handlePaymentOnPromoAddOrRemove({ action: 'remove' })
    } else {
      toastState.setToastMessage(
        response?.message ||
          i18nTranslate(
            'response.failureDeletePromo',
            'unable remove the promo code'
          ),
        false
      )
    }
    if (response.status === 'failure') {
      toastState.setToastMessage(
        i18nTranslate(
          'response.notExistedPromo',
          'The promotion codes requested do not exist in the cart.'
        ),
        false
      )
    }
    await this.loadPromoCodeList()
  }

  renderSubmitButton = () => {
    return (
      <Button
        data-testid="qa-pay-promo-apply"
        className={`rounded-0 promo-apply-button promo-code w-100 text-capitalize btn-bg-blue promo-code-btn`}
        variant="primary"
        disabled={this.disablePromotion()}
        type="submit">
        {i18nTranslate('couponCode.submit', 'apply')}
      </Button>
    )
  }

  renderPromos = promotionDetails => {
    return (
      <Button
        className="promo-code mr-1 mb-1"
        variant="outline-dark"
        data-testid="qa-promo-remove"
        onClick={() => this.handleDeleteCouponCode(promotionDetails.code)}>
        <span> {promotionDetails.code}</span>
        <CloseIcon size="2rem" className="promo-close-icon" />
      </Button>
    )
  }

  handlePaymentOnPromoAddOrRemove = async (options = {}) => {
    const { action = '' } = options
    const {
      isStoreCredit = false,
      onContinue = function () {},
      updateFlowPaths = function () {},
    } = this.props

    const { payments = [], value = {} } =
      (await cartContainer?.cartResponse) || {}
    let grandTotal = value?.grandTotal || 0

    let activePath = checkoutContainer.activePath

    /**
     * if payment is deleted, mobile venmo payment is not available
     * so delete payment should not trigger for mobile venmo payment redirection
     */
    let isFromVenmoPayment = getLocalStorage('isFromVenmoPayment') || false
    const isVenmoPayment =
      payments?.some(payment => {
        const type = payment?.type?.toLowerCase()
        return type === 'venmo'
      }) || false
    const isNotFromMobileRedirectVenmoPayment = !(
      isFromVenmoPayment && isVenmoPayment
    )

    let paymentDeleted = false
    if (Array.isArray(payments) && payments.length > 0) {
      if (isNotFromMobileRedirectVenmoPayment) {
        await checkoutContainer.deletePayment()
      }
      paymentDeleted = true
    }
    let isCartHavingSubscriptionProduct = cartContainer.hasSubscriptionProduct()

    let paymentOption = isStoreCredit ? 'storecredit' : 'payment'
    if (action === 'add' || action === 'remove') {
      if (grandTotal > 0) {
        updateFlowPaths()
        if (
          activePath === 'submitOrder' ||
          activePath === 6 ||
          (activePath === 'subscription' && isCartHavingSubscriptionProduct) ||
          (activePath === '' && paymentDeleted)
        ) {
          checkoutContainer.activePath = paymentOption
        }
      } else if (grandTotal === 0) {
        updateFlowPaths()
        if (
          isCartHavingSubscriptionProduct &&
          (activePath === 'payment' || activePath === 'subscription')
        ) {
          checkoutContainer.activePath = 'payment'
        } else if (activePath === 'payment' || activePath === 'storecredit') {
          checkoutContainer.activePath = 'shippingMethods'
        } else if (
          activePath === 'submitOrder' ||
          activePath === 6 ||
          (activePath === '' && paymentDeleted)
        ) {
          checkoutContainer.activePath = 'shippingMethods'
          onContinue()
        }
      }
    }
  }

  disablePromotion = () => {
    const { math = {} } = this.props
    let grandTotal = math['grandTotal'] || 0
    if (this.isCallInProgress) {
      return true
    }
    if (grandTotal > 0) {
      return false
    } else {
      return true
    }
  }

  handleShowMore = () => {
    const isExpanding = this.showMore
    const totalPromotions = cartContainer?.promoCodesList?.length || 0
    const visiblePromotions = isExpanding
      ? totalPromotions
      : Math.min(this.showMoreDefaultLength, totalPromotions)
    const toggleLabel = isExpanding
      ? i18nTranslate('promocode.seeLess', 'See less')
      : i18nTranslate('promocode.seeMore', 'See more')
    const promotionText = i18nTranslate(
      'wallet.promosInWalet',
      'Promotions in your wallet'
    ).trim()
    const nextAnnouncement = `${toggleLabel}. ${visiblePromotions} ${promotionText}.`

    // Force SR re-announcement if the same message is produced consecutively.
    this.showMoreAnnouncement =
      this.showMoreAnnouncement === nextAnnouncement
        ? `${nextAnnouncement}\u00A0`
        : nextAnnouncement

    if (!this.showMore) {
      const section = document.querySelector('.promo-accordion')
      section?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    this.showMore = !this.showMore
  }
  handeleCustomBlur = fieldData => {
    const { values = {} } = fieldData
    const promoCode = values?.promoCode || ''
    if (values && promoCode?.length >= 1) {
      this.isToResetForm = true
    } else {
      this.isToResetForm = false
    }
  }
  renderPromoCodeList = () => {
    const appliedPromoCodeList =
      cartContainer?.cartResponse?.promotionDetails?.codes || ''
    const promoCodeList = cartContainer?.promoCodesList || []
    const promoListMaxLength = this.showMore
      ? this.showMoreDefaultLength
      : promoCodeList.length
    const promotionWallet = i18nTranslate(
      'wallet.promosInWalet',
      ' Promotions in your wallet'
    )
    const totalPromotionAvailable = promoCodeList.length + promotionWallet
    const anyApplied =
      Array.isArray(appliedPromoCodeList) && appliedPromoCodeList.length > 0
    const showAppliedPromoList = this.disablePromoStacking
      ? this.showAppliedPromoList
      : true
    return (
      <div>
        <div className="myaccount-promowallet-code-wrapper">
          {appliedPromoCodeList && showAppliedPromoList && (
            <div className={this.disablePromoStacking ? 'mb-2' : 'mb-4'}>
              <Card.Subtitle>
                {i18nTranslate(
                  'wallet.AppliedPromoMsg',
                  'Promotions currently applied in your cart'
                )}
              </Card.Subtitle>
              <div
                className="promowallet-code-list"
                data-testid="qa-applied-promocode">
                {appliedPromoCodeList.map(this.renderPromos)}
              </div>
            </div>
          )}
          {this.disablePromoStacking &&
            anyApplied &&
            this.showAppliedPromoList && (
              <div
                className="single-promo-message mb-3"
                data-testid="qa-single-promo-msg">
                {this.renderSinglePromoMessage()}
              </div>
            )}

          {promoCodeList?.length > 0 && (
            <div>
              <div
                className="sr-only"
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-testid="qa-promo-showmore-announcement">
                {this.showMoreAnnouncement}
              </div>
              <Card.Subtitle
                tabIndex={0}
                className={`mb-2`}
                aria-label={totalPromotionAvailable}>
                {totalPromotionAvailable}
              </Card.Subtitle>
              {promoCodeList?.length > 0 && (
                <div id="wallet-promo-list">
                  {promoCodeList
                    ?.slice(0, promoListMaxLength)
                    ?.map((promoCodeData, index) => {
                      const isApplied =
                        appliedPromoCodeList != '' &&
                        appliedPromoCodeList?.find(
                          promo => promo.code === promoCodeData.code
                        )
                      const isInEligibleOffer =
                        isFetchAllPromoCodeEnabled() &&
                        promoCodeData?.isEligibleOffer === false
                      const isApplyDisabled =
                        isApplied?.code ||
                        this.disablePromotion() ||
                        isInEligibleOffer

                      let {
                        date = '',
                        month = '',
                        year = '',
                      } = dateFormatDeps.convertTimeStampToDate(
                        promoCodeData.expiryDate,
                        false
                      )
                      return (
                        <div key={index} className="promo-code-list-container">
                          <div tabIndex={0}>
                            <div
                              className="promo-code-exp-date"
                              data-testid="qa-promo-code-exp-date">
                              {i18nTranslate('promoCode.expire', 'Expire')}:{' '}
                              {month + '/' + date + '/' + year}
                            </div>
                            <div
                              className="promo-code-name"
                              data-testid="qa-promo-code-name">
                              {promoCodeData.code}
                            </div>
                          </div>
                          <div>
                            <Button
                              data-testid="qa-pay-promocode-apply"
                              className={`promo-apply-btn text-capitalize ${
                                isApplied?.code
                                  ? 'promo-apply-btn-disabled'
                                  : ''
                              }`}
                              variant="primary"
                              disabled={isApplyDisabled}
                              onClick={() =>
                                this.handleApplyClick(promoCodeData.code)
                              }>
                              {isApplied?.code
                                ? i18nTranslate('promocode.Applied', 'Applied')
                                : i18nTranslate('promocode.Apply', 'Apply')}
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                </div>
              )}
              {promoCodeList?.length > this.showMoreDefaultLength && (
                <div className="show-more-container">
                  {' '}
                  <Button
                    data-testid="qa-pay-promo-showmore"
                    aria-expanded={!this.showMore}
                    aria-controls="wallet-promo-list"
                    className={`show-more-btn`}
                    variant="primary"
                    onClick={this.handleShowMore}>
                    {' '}
                    {this.showMore
                      ? i18nTranslate('promocode.seeMore', 'See more')
                      : i18nTranslate('promocode.seeLess', 'See less')}{' '}
                    {this.showMore ? (
                      <DownwardArrowIcon aria-hidden={true} />
                    ) : (
                      <UpwardArrowIcon aria-hidden={true} />
                    )}{' '}
                  </Button>{' '}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  render() {
    return <>{this.renderPromoCodeList()}</>
  }
}

export { MyaccountWallet }
export default MyaccountWallet
